import { AgGridReact } from "ag-grid-react";
import React, { useEffect, useState } from "react";
import { deletevoucher, listvouchers, postvoucher, reconcilevouchers } from "../../../../services/admin";
import moment from "moment/moment";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { isAdmin } from "../../../../utils/roles";

export default function AllVouchers() {
    const [filterProduct, setFilterProduct] = useState([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        fetchVouchers();
    }, []);

    const fetchVouchers = () => {
        setLoading(true);
        const branchId = localStorage.getItem("branch");
        listvouchers(branchId).then((res) => {
            if (res.status === true) {
                setFilterProduct(res.data.details);
            }
            setLoading(false);
        }).catch(() => {
            setLoading(false);
        });
    };

    const columnDefs = [
        {
            headerName: "Actions",
            field: "id",
            width: 180,
            sortable: false,
            resizable: false,
            filter: false,
            floatingFilter: false,
            cellStyle: { textAlign: "center" },
            cellRenderer: function (params) {
                return (
                    <div className="text-center">
                        <a
                            onClick={() => navigate(`/accounts/viewvoucher/${params.value}`)}
                            className="btn-sm btn-info me-2"
                            title="View Voucher"
                        >
                            <i className="fa fa-eye"></i>
                        </a>
                        {params.data.status === "Draft" && isAdmin() && (
                            <>
                                <a
                                    onClick={() => navigate(`/accounts/editvoucher/${params.value}`)}
                                    className="btn-sm btn-warning me-2"
                                    title="Edit Voucher"
                                >
                                    <i className="fa fa-edit"></i>
                                </a>
                                <a
                                    onClick={() => handleDelete(params.value)}
                                    className="btn-sm btn-danger me-2"
                                    title="Delete Voucher"
                                >
                                    <i className="fa fa-trash"></i>
                                </a>
                                <a
                                    onClick={() => handlePost(params.value)}
                                    className="btn-sm btn-success me-2"
                                    title="Post Voucher"
                                >
                                    <i className="fa fa-check"></i>
                                </a>
                            </>
                        )}
                    </div>
                );
            },
        },
        {
            headerName: "Voucher Number",
            field: "voucher_number",
            width: 150,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" },
        },
        {
            headerName: "Voucher Type",
            field: "voucher_type.type_name",
            width: 150,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" },
            valueGetter: function (params) {
                return params.data.voucher_type ? params.data.voucher_type.type_name : "";
            },
        },
        {
            headerName: "Date",
            field: "voucher_date",
            width: 120,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" },
            cellRenderer: function (params) {
                if (params.value !== null) {
                    return moment(params.value).format("DD-MM-YYYY");
                }
            },
        },
        {
            headerName: "Amount",
            field: "total_amount",
            width: 120,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "right" },
            cellRenderer: function (params) {
                return params.value ? parseFloat(params.value).toFixed(2) : "0.00";
            },
        },
        {
            headerName: "Payment Mode",
            field: "payment_mode",
            width: 120,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" },
        },
        {
            headerName: "Narration",
            field: "narration",
            width: 250,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" },
        },
        {
            headerName: "Status",
            field: "status",
            width: 120,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "center" },
            cellRenderer: function (params) {
                const colors = {
                    Draft: "secondary",
                    Posted: "primary",
                    Approved: "success",
                    Cancelled: "danger",
                };
                return <span className={`badge bg-${colors[params.value]}`}>{params.value}</span>;
            },
        },
    ];

    const handleDelete = (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You want to delete this voucher?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        }).then((result) => {
            if (result.isConfirmed) {
                deletevoucher(id).then((res) => {
                    if (res.status === true) {
                        toast.success(res.message);
                        fetchVouchers();
                    }
                });
            }
        });
    };

    // A collection voucher is auto-posted, so the row Delete button will not
    // take it — it only accepts drafts. When a collection is deleted its
    // voucher can be left behind, showing a receipt on a day nothing was
    // collected and holding that amount in the cash account. This lists those
    // first, then clears them once confirmed.
    const handleRecalculate = () => {
        const branchId = localStorage.getItem("branch");
        setLoading(true);

        reconcilevouchers(branchId, true).then((res) => {
            setLoading(false);

            if (res?.status !== true) {
                return;
            }

            const orphans = res.data?.orphans || [];

            if (orphans.length === 0) {
                Swal.fire({
                    title: "Nothing to recalculate",
                    text: "Every voucher still matches a collection.",
                    icon: "success",
                });
                return;
            }

            const rows = orphans
                .map(
                    (o) =>
                        `<tr><td style="text-align:left">${o.voucher_number}</td>` +
                        `<td>${o.voucher_date ? moment(o.voucher_date).format("DD-MM-YYYY") : ""}</td>` +
                        `<td style="text-align:right">${parseFloat(o.total_amount).toFixed(2)}</td></tr>`
                )
                .join("");

            Swal.fire({
                title: "Recalculate vouchers?",
                html:
                    `<p>${orphans.length} voucher(s) totalling ` +
                    `${parseFloat(res.data.total_amount).toFixed(2)} no longer have a collection behind them.</p>` +
                    `<table style="width:100%;font-size:13px" border="1" cellpadding="4" cellspacing="0">` +
                    `<thead><tr><th>Voucher</th><th>Date</th><th>Amount</th></tr></thead>` +
                    `<tbody>${rows}</tbody></table>` +
                    `<p class="mt-2">They will be removed and the account balances corrected.</p>`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes, recalculate",
            }).then((result) => {
                if (!result.isConfirmed) {
                    return;
                }

                setLoading(true);
                reconcilevouchers(branchId, false).then((runres) => {
                    setLoading(false);
                    if (runres?.status === true) {
                        toast.success(runres.message);
                        fetchVouchers();
                    }
                });
            });
        }).catch(() => {
            setLoading(false);
        });
    };

    const handlePost = (id) => {
        Swal.fire({
            title: "Post Voucher?",
            text: "Are you sure you want to post this voucher? This will update account balances.",
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, post it!",
        }).then((result) => {
            if (result.isConfirmed) {
                postvoucher(id).then((res) => {
                    if (res.status === true) {
                        toast.success(res.message);
                        fetchVouchers();
                    }
                });
            }
        });
    };

    return (
        <div>
            <div className="page-header">
                <h2 className="main-content-title tx-24 mg-b-5">All Vouchers</h2>
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <a>Accounts</a>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                        All Vouchers
                    </li>
                </ol>
            </div>

            {loading && (
                <div className="loader-wrapper">
                    <div className="loader"></div>
                    <h3>Loading...Please do not go back or close the window.</h3>
                </div>
            )}

            {!loading && (
                <div className="content-body">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-header">
                                        <div className="row">
                                            <div className="col-6">
                                                <h4 className="card-title">All Vouchers</h4>
                                            </div>
                                            <div className="col-6 text-end">
                                                {isAdmin() && (
                                                    <>
                                                        <button
                                                            className="btn btn-warning me-2"
                                                            onClick={handleRecalculate}
                                                            title="Remove vouchers whose collection was deleted"
                                                        >
                                                            <i className="fa fa-refresh"></i> Recalculate
                                                        </button>
                                                        <button
                                                            className="btn btn-primary"
                                                            onClick={() => navigate("/accounts/addvoucher")}
                                                        >
                                                            <i className="fa fa-plus"></i> Create Voucher
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        <div className="ag-theme-alpine" style={{ height: 600, width: "100%" }}>
                                            <AgGridReact
                                                rowData={filterProduct}
                                                columnDefs={columnDefs}
                                                defaultColDef={{
                                                    sortable: true,
                                                    resizable: true,
                                                }}
                                                pagination={true}
                                                paginationPageSize={20}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
